import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import Button from "shared/components/FormElements/Button";
import CheckBox from "shared/components/FormElements/CheckBox";
import NotFound from "shared/components/UIElements/NotFound";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { isActiveOptions, ownerShipTypes, yesNoUnknowTypes, plateTypes } from "shared/util/types";
import DatePicker from "shared/components/FormElements/DatePicker";
import { PERMISSION_TYPES } from "shared/util/constants";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";

const InventoryFuelItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentDriver, setCurrentDriver] = useState();
  const [currentDriverKey, setCurrentDriverKey] = useState();

  const setItemState = (item, value, valid, setState) => {
    setState(item + "-" + Math.random());
    inputHandler(item, value, true);
    setLoadedItem((prevItem) => {
      return {
        ...prevItem,
        [item]: value,
      };
    });
  };

  const { fuels, vehicles, driverOptions, UpdateDriver } = useContext(StoreContext);

  const itemType = "fuel";
  const endPointUpdate = "fuels";
  const endPointDelete = "fuels";
  const endPointCreate = "fuels";

  // Initialize form with all fields from the schema
  const [formState, inputHandler, setFormData] = useForm(
    {
      vehicle: { value: "", isValid: true },
      driver: { value: "", isValid: true },
      date: { value: "", isValid: false },
      amount: { value: "", isValid: false },
      note: { value: "", isValid: true },
    },
    false
  );

  // Fetching item data
  useEffect(() => {
    const fetchItem = async () => {
      if (!isNewItem) {
        setIsItemLoaded(false);
        try {
          const fuel = fuels.find((fuel) => fuel.id === itemId);
          if (fuel) {
            setLoadedItem(fuel);
            if (fuel.driver) {
              // setItemState("driver", fuel.driver, true, setCurrentDriverKey);
            }
          }
        } catch (err) {
          console.log(err);
        }
        setIsItemLoaded(true);
      } else {
        setIsItemLoaded(true);
      }
    };
    fetchItem();
  }, [itemId, fuels, vehicles, isNewItem, setFormData]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();
    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    // Build the object to send (spelled out)
    const infoToBeSent = {
      vehicle: formState.inputs.vehicle.value,
      driver: formState.inputs.driver.value,
      date: formState.inputs.date.value,
      note: formState.inputs.note.value,
      amount: formState.inputs.amount.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      window.toast.error(t("httpCodes." + err.code));
    }
  };

  const updateCurrentDriver = useCallback((val) => {
    const comingDriver = vehicles.find((vehicle) => vehicle.id.toString() === val.toString())?.driver;
    setItemState("driver", comingDriver, true, setCurrentDriverKey);
  });

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  if (!isItemLoaded && !isNewItem) {
    return <p>Loading...</p>;
  }

  if (!isNewItem && isItemLoaded && !loadedItem && !error) {
    return <NotFound item={itemType} />;
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" style={{ maxWidth: "800px" }} onSubmit={itemUpdateSubmitHandler}>
          {/* <div className="horizontal-flex"> */}
          <CustomSelect
            id="vehicle"
            options={vehicles.map((vehicle) => ({
              label: vehicle.plate,
              value: vehicle.id,
            }))}
            onInput={inputHandler}
            label="Araç"
            initialValue={
              vehicles.find((vehicle) => vehicle.id.toString() === loadedItem?.vehicle?.toString())
                ? {
                    label:
                      vehicles.find((vehicle) => vehicle.id?.toString() === loadedItem?.vehicle?.toString())?.plate ||
                      "",
                    value:
                      vehicles.find((vehicle) => vehicle.id?.toString() === loadedItem?.vehicle?.toString())?.id || "",
                  }
                : null
            }
            validators={[VALIDATOR_REQUIRE()]}
            initialValid={true}
            fireChange={(val) => updateCurrentDriver(val)}
          ></CustomSelect>

          <Input
            key={currentDriverKey}
            id="driver"
            type="text"
            element="input"
            onInput={inputHandler}
            label="Sürücü"
            initialValue={driverOptions.find((option) => option.value === loadedItem?.driver)?.label || "-"}
            validators={[VALIDATOR_NONE()]}
            initialValid={true}
            disabled={true}
          ></Input>
          {/* </div>
          <div className="horizontal-flex"> */}
          <DatePicker
            id="date"
            onInput={inputHandler}
            label="Tarih"
            initialValid={true}
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem?.date?.toString() || ""}
          ></DatePicker>

          <Input
            id="amount"
            element="input"
            type="text"
            label="Ödenen"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Ödenen alanı zorunludur."
            initialValue={loadedItem?.amount || ""}
            initialValid={true}
            onInput={inputHandler}
          />
          {/* </div> */}

          {/* <div className="horizontal-flex"> */}
          <Input
            id="note"
            element="textarea"
            type="text"
            label="Notlar"
            rows={3}
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem?.note || ""}
            initialValid={true}
            onInput={inputHandler}
          />
          {/* </div> */}
          {auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY] && (
            <FormFooter
              isNewItem={isNewItem}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={
                !formState.isValid || !formState.inputs.vehicle.value || !formState.inputs.amount.value || isLoading
              }
            />
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default InventoryFuelItem;
